import { map, when, evolve, propEq, set, lensProp, find, compose } from 'ramda';
import { LOCATION_CHANGE } from 'connected-react-router';
import { childReducerWrapper } from '../../helpers/reducer-helper';
import searchRedirectReducer from './search-redirect-reducer';
import { FETCH_SEARCH_REQUEST, FETCH_SEARCH_SUCCESS, FETCH_SEARCH_FAIL, FETCH_SEARCH_PARTS_REQUEST, FETCH_SEARCH_PARTS_SUCCESS, FETCH_SEARCH_PARTS_FAIL, SET_SEARCH_PARTS_UNSUBSCRIBE, FETCH_TRIP_POINTS_REQUEST, FETCH_TRIP_POINTS_SUCCESS, FETCH_TRIP_POINTS_FAIL, FETCH_TRIP_SEATS_REQUEST, FETCH_TRIP_SEATS_SUCCESS, FETCH_TRIP_SEATS_FAIL, FETCH_TRIP_SUBMIT_REQUEST, FETCH_TRIP_SUBMIT_SUCCESS, FETCH_TRIP_SUBMIT_FAIL, SET_OPENING_TRIP, RESET_OPENING_TRIP, SET_SEARCH_PASSENGERS, SET_SEAT_SELECTED, SET_SEAT_DESELECTED, SET_SEATS_AS_FREE, SET_PASSENGERS_REDIRECT, OPEN_TRIP_DETAILS, CLOSE_TRIP_DETAILS } from '../constants/actions';
import { getTripWithComputedTime } from '../selectors/trips';
import { initialState, getTripWithDefaults } from './utils';
const {
  searchState
} = initialState;
export default function search(state = searchState, action, ...args) {
  switch (action.type) {
    case FETCH_SEARCH_REQUEST:
      return {
        ...state,
        fetching: true,
        completed: false,
        trips: [],
        errors: null
      };
    case FETCH_SEARCH_SUCCESS:
      {
        const {
          id,
          completed,
          trips
        } = action.data;
        return {
          ...state,
          id,
          directionId: action.data['direction-id'],
          distance: action.data.distance,
          firstLoad: false,
          fetching: false,
          completed,
          trips: trips ? trips.map(getTripWithDefaults) : [],
          nearCities: action.data['nearby-trips'],
          errors: null,
          meta: action.meta
        };
      }
    case FETCH_SEARCH_FAIL:
      return {
        ...state,
        firstLoad: false,
        fetching: false,
        completed: true,
        errors: action.errors
      };
    case FETCH_SEARCH_PARTS_REQUEST:
      return {
        ...state,
        fetching: false,
        completed: false
      };
    case FETCH_SEARCH_PARTS_SUCCESS:
      {
        if (state.id !== action.data.id) return state;
        const currTrips = state.trips;
        const stateTripsIds = currTrips.map(({
          id
        }) => id);
        const newTrips = (action.data.trips || []).filter(trip => !stateTripsIds.includes(trip.id));
        const recalculatedCurrTrips = newTrips.length ? currTrips.map(getTripWithComputedTime) : currTrips;
        return evolve({
          fetching: () => false,
          completed: () => action.data.completed,
          trips: () => newTrips.map(getTripWithDefaults).concat(recalculatedCurrTrips),
          errors: () => null
        }, state);
      }
    case FETCH_SEARCH_PARTS_FAIL:
      return {
        ...state,
        firstLoad: false,
        fetching: false,
        completed: true,
        errors: state.trips.length === 0 ? action.errors : null
      };
    case SET_SEARCH_PARTS_UNSUBSCRIBE:
      return {
        ...state,
        searchPartsUnsubscribe: action.searchPartsUnsubscribe
      };
    case FETCH_TRIP_POINTS_REQUEST:
      return evolve({
        trips: map(when(propEq('id', action.tripId), set(lensProp('points'), {
          fetching: true,
          data: []
        })))
      }, state);
    case FETCH_TRIP_POINTS_SUCCESS:
      return evolve({
        trips: map(when(propEq('id', action.tripId), evolve({
          points: () => ({
            received: true,
            fetching: false,
            data: action.data
          })
        })))
      }, state);
    case FETCH_TRIP_POINTS_FAIL:
      return state;
    // suppress error
    case FETCH_TRIP_SEATS_REQUEST:
      return evolve({
        trips: map(when(propEq('id', action.tripId), set(lensProp('seats'), {
          fetching: true,
          data: [],
          errors: null
        })))
      }, state);
    case FETCH_TRIP_SEATS_SUCCESS:
      return evolve({
        trips: map(when(propEq('id', action.tripId), evolve({
          seats: () => ({
            received: true,
            fetching: false,
            data: action.data
          }),
          selectedSeats: () => action.data.map(() => [])
        })))
      }, state);
    case FETCH_TRIP_SEATS_FAIL:
      return evolve({
        trips: map(when(propEq('id', action.tripId), set(lensProp('seats'), {
          fetching: false,
          data: [],
          errors: action.errors
        })))
      }, state);
    case FETCH_TRIP_SUBMIT_REQUEST:
      return evolve({
        trips: map(when(propEq('id', action.tripId), compose(set(lensProp('isSubmitted'), true), set(lensProp('errors'), null))))
      }, state);
    case FETCH_TRIP_SUBMIT_SUCCESS:
      return state;
    // redirects from TripContatiner
    case FETCH_TRIP_SUBMIT_FAIL:
      return evolve({
        trips: map(when(propEq('id', action.tripId), set(lensProp('errors'), action.errors)))
      }, state);
    case SET_SEARCH_PASSENGERS:
      return {
        ...state,
        passengers: action.passengers
      };
    case SET_OPENING_TRIP:
      return {
        ...state,
        openingTrip: action.tripId
      };
    case RESET_OPENING_TRIP:
      return {
        ...state,
        openingTrip: null
      };
    case SET_SEAT_SELECTED:
      return evolve({
        trips: map(when(propEq('id', action.tripId), evolve({
          selectedSeats: selectedSeats => selectedSeats.map((seats, idx) => {
            if (idx !== action.idx) {
              return seats;
            }
            if (seats.length === action.maxSeatsToSelect) {
              return [...seats.slice(1), action.seat];
            }
            return [...seats, action.seat];
          })
        })))
      }, state);
    case SET_SEAT_DESELECTED:
      return evolve({
        trips: map(when(propEq('id', action.tripId), evolve({
          selectedSeats: selectedSeats => selectedSeats.map((seats, idx) => {
            if (idx !== action.idx) return seats;
            const seat = find(propEq('id', action.seat.id), seats);
            seats.splice(seats.indexOf(seat), 1);
            return [...seats];
          })
        })))
      }, state);
    case SET_PASSENGERS_REDIRECT:
      return {
        ...state,
        passengers: action.newPassengers,
        redirect: searchRedirectReducer(state.redirect, action, ...args)
      };
    case SET_SEATS_AS_FREE:
      {
        const {
          tripId,
          mapFreeSeats
        } = action;
        // помечаем определенные места как свободные
        return evolve({
          trips: map(when(propEq('id', tripId), evolve({
            seats: {
              data: map(seatMap => {
                const {
                  seats,
                  id
                } = seatMap;
                const freeSeats = mapFreeSeats[id];
                if (!freeSeats) {
                  return seatMap;
                }
                return {
                  ...seatMap,
                  seats: seats.map(seatRow => seatRow.map(seat => {
                    if (!freeSeats.includes(seat.id)) {
                      return seat;
                    }
                    return {
                      ...seat,
                      type: 'free'
                    };
                  }))
                };
              })
            }
          })))
        }, state);
      }
    case OPEN_TRIP_DETAILS:
      return {
        ...state,
        openTripDetailsId: action.tripId
      };
    case CLOSE_TRIP_DETAILS:
      return {
        ...state,
        openTripDetailsId: null
      };
    case LOCATION_CHANGE:
      {
        const {
          payload: {
            location: {
              state: locationState = {}
            }
          }
        } = action;
        return {
          ...state,
          from: locationState.from || state.from,
          to: locationState.to || state.to,
          on: locationState.on || state.on,
          passengers: locationState.passengers || state.passengers,
          openTripDetailsId: locationState.tripId,
          redirect: {
            ...state.redirect,
            showed: locationState.redirected || false
          }
        };
      }
    default:
      return childReducerWrapper({
        redirect: searchRedirectReducer
      }, state, action, ...args);
  }
}